import React, { useState } from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import Footer from '../Components/Footer';
import Breadcrumbs from '../Components/Breadcrumbs';
import { emailSignInMessage, googleSignInMessage } from '../utils/authMessages';

/**
 * Login Page - Authentication for Soap Lab
 */
export default function LoginPage() {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [resetSent, setResetSent] = useState(false);

  const { signIn, signInWithGoogle, resetPassword } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();

  // Where to redirect after login
  const from = location.state?.from?.pathname || '/';

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);

    try {
      const loggedInUser = await signIn(email, password);
      // If email not verified, redirect to verification page
      if (!loggedInUser.emailVerified) {
        navigate('/verify-email', { replace: true });
      } else {
        navigate(from, { replace: true });
      }
    } catch (err) {
      console.error(err);
      setError(emailSignInMessage(err));
    }

    setLoading(false);
  };

  const handleGoogleSignIn = async () => {
    setError('');
    setLoading(true);

    try {
      const result = await signInWithGoogle();
      if (result) {
        navigate(from, { replace: true });
      } else {
        setLoading(false);
      }
    } catch (err) {
      setError(googleSignInMessage(err));
      setLoading(false);
    }
  };

  const handleForgotPassword = async () => {
    if (!email) {
      setError('Please enter your email address first.');
      return;
    }

    setLoading(true);
    try {
      await resetPassword(email);
      setResetSent(true);
      setError('');
    } catch (err) {
      if (err.code === 'functions/resource-exhausted' || err.code === 'auth/too-many-requests') {
        setError('Too many reset requests. Please wait a while and try again.');
      } else if (err.code === 'functions/invalid-argument' || err.code === 'auth/invalid-email') {
        setError('Please enter a valid email address.');
      } else {
        setError('Failed to send reset email. Please try again.');
      }
    }
    setLoading(false);
  };

  return (
    <div className="sl-page auth-page">

      <div className="pageheader borderleft">
        <Breadcrumbs
          items={[
            { label: 'Home', to: '/' },
            { label: 'Login' }
          ]}
        />
        <div className="pageheading">Welcome back!</div>
      </div>

      <div className="auth-content borderleft">
        <div className="auth-container">
          <p className="auth-subtitle">Sign in to your account</p>


          {/* Error Message */}
          {error && (
            <div className="auth-error">
              <span>⚠️</span> {error}
            </div>
          )}

          {/* Reset Password Success */}
          {resetSent && (
            <div className="auth-success">
              <span>✅</span> If there’s a SoapLab account for that email, we’ve sent a link to reset your password. Check your inbox.
            </div>
          )}

          {/* Google Sign In */}
          <button
            onClick={handleGoogleSignIn}
            className="auth-google-btn"
            disabled={loading}
          >
            <svg width="18" height="18" viewBox="0 0 24 24">
              <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z" />
              <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z" />
              <path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z" />
              <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z" />
            </svg>
            Continue with Google
          </button>

          <div className="auth-divider">
            <span>or</span>
          </div>

          {/* Email Form */}
          <form onSubmit={handleSubmit} className="auth-form">
            <div className="form-group">
              <label htmlFor="email">Email</label>
              <input
                type="email"
                id="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="you@example.com"
                required
              />
            </div>

            <div className="form-group">
              <label htmlFor="password">Password</label>
              <input
                type="password"
                id="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                placeholder="••••••••"
                required
              />
            </div>

            <button
              type="button"
              className="forgot-password-btn"
              onClick={handleForgotPassword}
            >
              Forgot password?
            </button>

            <button
              type="submit"
              className="auth-submit-btn"
              disabled={loading}
            >
              {loading ? 'Signing in...' : 'Sign in'}
            </button>
          </form>

          <p className="auth-terms">
          New to SoapLab? Continuing with Google creates an account. By doing so you confirm you are 18 or older and agree to our{' '}
          <a href="/terms" target="_blank" rel="noopener noreferrer">Terms of Use</a> and{' '}
          <a href="/privacy" target="_blank" rel="noopener noreferrer">Privacy Policy</a>.
        </p>

        <p className="auth-switch">
            Don't have an account?{' '}
            <Link to="/signup">Sign up for free</Link>
          </p>
        </div>

        <aside className="auth-aside">
          <span className="sl-eyebrow">New to SoapLab?</span>
          <h2 className="sl-subtitle">Create a free account</h2>
          <ul className="auth-aside-list">
            <li>Full lye and melt &amp; pour calculators</li>
            <li>Save up to 10 recipes</li>
            <li>30 ingredients and 5 batch records</li>
            <li>Recipe templates and the community</li>
          </ul>
          <Link to="/signup" className="sl-btn sl-btn-secondary">Create a free account</Link>
        </aside>
      </div>
      <Footer />
    </div>
  );
}
